import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
} from "react";
import { createRoot, Root } from "react-dom/client";
import {
  Paper,
  Text,
  Group,
  ScrollArea,
  UnstyledButton,
  MantineProvider,
} from "@mantine/core";
import { SuggestionProps, SuggestionKeyDownProps } from "@tiptap/suggestion";

interface AbbreviationItem {
  shortcode: string;
  expansion: string;
}

interface SuggestionListProps {
  items: AbbreviationItem[];
  command: (item: AbbreviationItem) => void;
}

const SuggestionList = forwardRef<
  { onKeyDown: (props: SuggestionKeyDownProps) => boolean },
  SuggestionListProps
>(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  const selectItem = useCallback(
    (index: number) => {
      const item = items[index];
      if (item) {
        command(item);
      }
    },
    [items, command]
  );

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: SuggestionKeyDownProps) => {
      if (event.key === "ArrowUp") {
        setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
        return true;
      }
      if (event.key === "ArrowDown") {
        setSelectedIndex((prev) => (prev + 1) % items.length);
        return true;
      }
      if (event.key === "Enter") {
        selectItem(selectedIndex);
        return true;
      }
      if (event.key === "Escape") {
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) {
    return null;
  }

  const stripHtml = (html: string): string => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  return (
    <Paper
      shadow="md"
      withBorder
      p={4}
      style={{ minWidth: 280, maxWidth: 400 }}
    >
      <ScrollArea.Autosize mah={250}>
        {items.map((item, index) => (
          <UnstyledButton
            key={item.shortcode}
            onClick={() => selectItem(index)}
            p="xs"
            style={{
              display: "block",
              width: "100%",
              borderRadius: "var(--mantine-radius-sm)",
              backgroundColor:
                index === selectedIndex
                  ? "var(--mantine-color-blue-light)"
                  : "transparent",
            }}
          >
            <Group gap="sm" wrap="nowrap">
              <Text size="sm" fw={600} ff="monospace" style={{ flexShrink: 0 }}>
                {item.shortcode}
              </Text>
              <Text size="xs" c="dimmed" lineClamp={1} style={{ flex: 1 }}>
                {stripHtml(item.expansion)}
              </Text>
            </Group>
          </UnstyledButton>
        ))}
      </ScrollArea.Autosize>
    </Paper>
  );
});

SuggestionList.displayName = "SuggestionList";

export function createSuggestionRenderer() {
  let root: Root | null = null;
  let popup: HTMLDivElement | null = null;
  let componentRef: {
    onKeyDown: (props: SuggestionKeyDownProps) => boolean;
  } | null = null;

  return {
    onStart: (props: SuggestionProps<AbbreviationItem>) => {
      popup = document.createElement("div");
      popup.setAttribute("data-text-expansion-popup", "true");
      popup.style.position = "absolute";
      popup.style.zIndex = "9999";
      popup.addEventListener("mousedown", (e) => {
        e.preventDefault();
      });
      document.body.appendChild(popup);

      root = createRoot(popup);
      root.render(
        <MantineProvider>
          <SuggestionList
            items={props.items}
            command={props.command}
            ref={(ref) => {
              componentRef = ref;
            }}
          />
        </MantineProvider>
      );

      updatePosition(props);
    },

    onUpdate: (props: SuggestionProps<AbbreviationItem>) => {
      if (root && popup) {
        root.render(
          <MantineProvider>
            <SuggestionList
              items={props.items}
              command={props.command}
              ref={(ref) => {
                componentRef = ref;
              }}
            />
          </MantineProvider>
        );
        updatePosition(props);
      }
    },

    onKeyDown: (props: SuggestionKeyDownProps) => {
      if (props.event.key === "Escape") {
        cleanup();
        return true;
      }
      return componentRef?.onKeyDown(props) ?? false;
    },

    onExit: () => {
      cleanup();
    },
  };

  function updatePosition(props: SuggestionProps<AbbreviationItem>) {
    if (!popup || !props.clientRect) return;
    const rect = props.clientRect();
    if (!rect) return;
    popup.style.left = `${rect.left + window.scrollX}px`;
    popup.style.top = `${rect.bottom + window.scrollY + 4}px`;
  }

  function cleanup() {
    if (root) {
      root.unmount();
      root = null;
    }
    if (popup) {
      popup.remove();
      popup = null;
    }
    componentRef = null;
  }
}
